import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import { useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Clock, Route as RouteIcon, Users } from "lucide-react";

const stopIcon = (n) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#0020C2;color:#fff;width:24px;height:24px;border-radius:50%;border:2.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;font-family:monospace;">${n}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const destIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#DC2626;color:#fff;padding:3px 8px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);font-size:10px;font-weight:800;letter-spacing:1px;white-space:nowrap;">HEDEF</div>`,
  iconSize: [52, 22],
  iconAnchor: [26, 11],
});

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [30, 30] });
  }, [points, map]);
  return null;
};

// Güzergahı haritada göster: rota çizgisi + numaralı duraklar + hedef + yürüme mesafeleri
export const RouteMapModal = ({ route, onStopMove = null, onClose }) => {
  const stops = route.stops || [];
  const line = (route.geometry || []).map(([lng, lat]) => [lat, lng]);
  const points = [
    ...stops.map((s) => [s.lat, s.lng]),
    ...(route.dest ? [[route.dest.lat, route.dest.lng]] : []),
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-[1200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="route-map-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-[600]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Güzergah Haritası</div>
            <div className="font-heading font-bold text-lg">{route.name}</div>
            <div className="flex items-center gap-4 mt-1 text-xs text-neutral-600">
              <span className="flex items-center gap-1"><RouteIcon className="w-3.5 h-3.5 text-[#0020C2]" /> <b className="num">{route.estimated_km}</b> km</span>
              {route.duration_min ? <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#0020C2]" /> <b className="num">{route.duration_min}</b> dk</span> : null}
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#0020C2]" /> <b className="num">{(route.personnel_ids || []).length}</b> kişi · <b className="num">{stops.length}</b> durak</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="route-map-close"><X className="w-4 h-4" /></button>
        </div>

        <div style={{ height: 420 }}>
          <MapContainer center={points[0] || [39.9334, 32.8597]} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#0020C2", weight: 4, opacity: 0.8 }} />}
            {stops.map((s, i) => (
              <Marker
                key={i}
                position={[s.lat, s.lng]}
                icon={stopIcon(i + 1)}
                draggable={Boolean(onStopMove)}
                eventHandlers={onStopMove ? {
                  dragend: (e) => {
                    const ll = e.target.getLatLng();
                    if (window.confirm(`Durak ${i + 1} buraya taşınsın mı? (yola oturtulur, yürüme mesafeleri ve saatler yeniden hesaplanır)`)) {
                      onStopMove(route.id, i, ll.lat, ll.lng);
                    } else {
                      e.target.setLatLng([s.lat, s.lng]);
                    }
                  },
                } : {}}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold mb-1">Durak {i + 1}{s.pickup_time ? ` · ${s.pickup_time}` : ""} · {s.count} kişi</div>
                    {(s.personnel || []).map((p) => (
                      <div key={p.id} className="flex justify-between gap-3 py-0.5">
                        <span>{p.name}</span>
                        <span className="text-neutral-500">yürüme {p.walk_m} m</span>
                      </div>
                    ))}
                  </div>
                </Popup>
              </Marker>
            ))}
            {route.dest && (
              <Marker position={[route.dest.lat, route.dest.lng]} icon={destIcon}>
                <Popup><div className="text-xs font-semibold">{route.dest.label || "Hedef"}</div></Popup>
              </Marker>
            )}
          </MapContainer>
        </div>

        {/* Durak listesi */}
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-600 mb-2">Durak Sırası ve Yürüme Mesafeleri</div>
          <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
            {stops.map((s, i) => (
              <div key={i} className="px-4 py-2.5" data-testid={`route-stop-${i}`}>
                <div className="flex items-center gap-2 text-xs font-bold mb-1">
                  <span className="w-5 h-5 bg-[#0020C2] text-white rounded-full flex items-center justify-center text-[10px] num">{i + 1}</span>
                  Durak {i + 1} <span className="text-neutral-400 font-normal">· {s.count} kişi</span>
                </div>
                <div className="pl-7 space-y-0.5">
                  {(s.personnel || []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs">
                      <span>{p.name} <span className="text-neutral-400">{p.address ? `— ${p.address}` : ""}</span></span>
                      <span className={`num font-semibold ${p.walk_m > 400 ? "text-[#DC2626]" : p.walk_m > 200 ? "text-[#D97706]" : "text-[#16A34A]"}`}>{p.walk_m} m yürüme</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {route.dest && (
              <div className="px-4 py-2.5 bg-[#FEF2F2] flex items-center gap-2 text-xs font-bold">
                <span className="px-1.5 py-0.5 bg-[#DC2626] text-white text-[9px] uppercase tracking-widest">Hedef</span>
                {route.dest.label || "Varış noktası"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
